import { Link } from "react-router-dom";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import { formatDateDE, FILES_BASE, formatBytes } from "@/lib/api";
import { Download, Image as ImageIcon, FileText, ArrowRight, Factory } from "lucide-react";

export default function UpdateCard({ update }) {
  const attachments = update.attachments || [];
  const previewImage = attachments.find((a) => a.is_image);

  return (
    <article
      className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-200"
      data-testid={`update-card-${update.id}`}
    >
      {previewImage && (
        <Link to={`/updates/${update.id}`} className="block aspect-[16/7] overflow-hidden bg-slate-100">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${previewImage.url}`}
            alt={update.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </Link>
      )}

      <div className="p-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {formatDateDE(update.date)}
          </span>
          <span className="text-slate-300">•</span>
          <StatusBadge status={update.status} />
          <CategoryBadge category={update.category} />
        </div>

        <Link to={`/updates/${update.id}`} className="block">
          <h3
            className="font-heading text-2xl font-semibold text-slate-900 leading-tight mb-2 group-hover:text-[#E2001A] transition-colors"
            data-testid={`update-title-${update.id}`}
          >
            {update.title}
          </h3>
        </Link>

        <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">
          {update.description}
        </p>

        {update.anlagen && update.anlagen.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Factory className="h-3.5 w-3.5 text-slate-400" />
            {update.anlagen.map((a) => (
              <span
                key={a.id}
                className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
              >
                {a.name}
              </span>
            ))}
          </div>
        )}

        {attachments.length > 0 && (
          <div className="border-t border-slate-100 pt-4 space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Dateien ({attachments.length})
            </div>
            {attachments.slice(0, 3).map((a) => (
              <a
                key={a.id}
                href={`${FILES_BASE}/${a.id}`}
                target="_blank"
                rel="noreferrer"
                download={a.original_name}
                className="flex items-center justify-between gap-2 group/dl px-2 py-1.5 rounded hover:bg-slate-50 transition-colors"
                data-testid={`download-attachment-${a.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {a.is_image ? (
                    <ImageIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  ) : (
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  )}
                  <span className="text-sm text-slate-700 truncate">{a.original_name}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0">{formatBytes(a.size)}</span>
                </div>
                <Download className="h-4 w-4 text-slate-400 group-hover/dl:text-[#E2001A] flex-shrink-0" />
              </a>
            ))}
            {attachments.length > 3 && (
              <Link
                to={`/updates/${update.id}`}
                className="text-xs text-[#E2001A] hover:underline font-medium px-2"
              >
                +{attachments.length - 3} weitere Dateien
              </Link>
            )}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <Link
            to={`/updates/${update.id}`}
            className="inline-flex items-center text-sm font-semibold text-[#E2001A] hover:text-[#C20015]"
            data-testid={`update-detail-link-${update.id}`}
          >
            Details ansehen
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    </article>
  );
}
